'use client';

import * as React from 'react';
import Image from 'next/image';
import {
  ArrowUpRight,
  Captions,
  FileText,
  Mic,
  Monitor,
  Play,
  Scissors,
  Sparkles,
  Upload,
  Video
} from 'lucide-react';
import { motion } from 'motion/react';

import { routes } from '@workspace/routes';
import { Button } from '@workspace/ui/components/customButton';

import heroDecoClapper from '~/public/assets/hero/hero-deco-clapper.png';
import heroDecoPlay from '~/public/assets/hero/hero-deco-play.png';

import { trackCta } from '~/lib/track-cta';

const HERO_VIDEO = {
  embedUrl:
    'https://player.vimeo.com/video/1159090878?autoplay=1&title=0&byline=0&portrait=0',
  thumbnail: 'https://gw-ai-social-media.b-cdn.net/VideoMule/thumb8.png'
};

const scatteredIcons = [
  { Icon: Upload, x: '6%', y: '22%', rotate: -12, size: 20 },
  { Icon: Mic, x: '90%', y: '18%', rotate: 15, size: 22 },
  { Icon: Video, x: '4%', y: '58%', rotate: 8, size: 18 },
  { Icon: Scissors, x: '93%', y: '55%', rotate: -10, size: 20 },
  { Icon: FileText, x: '12%', y: '82%', rotate: 12, size: 16 },
  { Icon: Sparkles, x: '86%', y: '80%', rotate: -8, size: 18 },
  { Icon: Monitor, x: '16%', y: '38%', rotate: -15, size: 16 },
  { Icon: Captions, x: '82%', y: '40%', rotate: 10, size: 16 }
];

export default function HeroSection() {
  const [isPlaying, setIsPlaying] = React.useState(false);

  return (
    <section
      id="hero"
      className="relative py-0 pt-8 pb-4 overflow-hidden sm:pt-12 sm:pb-8 md:pt-20 md:pb-14"
    >
      <div className="absolute inset-0 pointer-events-none bg-gradient-hero-top" />

      {scatteredIcons.map(({ Icon, x, y, rotate, size }, i) => (
        <motion.div
          key={i}
          className="absolute items-center justify-center hidden w-10 h-10 lg:flex rounded-xl text-muted-foreground/30"
          style={{ left: x, top: y, rotate: `${rotate}deg` }}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 + i * 0.1 }}
        >
          <Icon size={size} />
        </motion.div>
      ))}

      <div className="relative max-w-4xl px-4 mx-auto text-center md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <span className="inline-flex items-center px-3 py-1 mb-4 text-xs font-medium border rounded-full border-primary/20 text-primary sm:mb-6 bg-videomule-light-green dark:bg-primary/10">
            #1 AI Tutorial Maker
          </span>

          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-extrabold tracking-tight text-foreground leading-[1.1] mb-4 sm:mb-6">
            Create Product Demos
            <br />& Training Videos in Minutes
            <span className="text-primary">.</span>
          </h1>

          <p className="max-w-2xl px-2 mx-auto mb-6 text-sm leading-relaxed sm:text-base md:text-lg text-muted-foreground sm:mb-10">
            Upload your raw screen capture and let VideoMule write the script,
            generate a professional voiceover, and sync it to your video
            automatically.
          </p>

          <div className="flex items-center justify-center">
            <Button
              size="lg"
              href={routes.dashboard.auth.SignUp}
              onClick={() => trackCta('cta_signup_clicked', { surface: 'homepage_hero' })}
              className="gap-3 text-base font-semibold group sm:text-lg"
            >
              Start Free Trial
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-videomule-white/20">
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Browser Mockup with Dashboard */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="relative px-4 mx-auto mt-10 max-w-7xl sm:mt-14 md:px-8"
      >
        <div className="relative w-full aspect-video">
          {/* Decorative Play Icon - Top Left */}
          <div className="absolute z-10 hidden -top-8 -left-12 sm:-top-10 sm:-left-16 animate-float sm:block">
            <Image
              src={heroDecoPlay}
              alt="Video play icon symbolizing tutorial or media playback"
              className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24"
            />
          </div>

          {/* Decorative Clapperboard Icon - Bottom Right */}
          <div className="absolute z-10 hidden -bottom-8 -right-8 sm:-right-12 animate-float sm:block">
            <Image
              src={heroDecoClapper}
              alt="Clapperboard icon symbolizing video creation and editing"
              className="w-14 h-14 sm:w-18 sm:h-18 lg:w-22 lg:h-22"
            />
          </div>
          {isPlaying ? (
            <div className="w-full h-full overflow-hidden bg-black rounded-2xl">
              <iframe
                src={HERO_VIDEO.embedUrl}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                loading="lazy"
                className="w-full h-full"
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setIsPlaying(true)}
              className="relative w-full h-full cursor-pointer group"
            >
              <Image
                src={HERO_VIDEO.thumbnail}
                alt="VideoMule Demo"
                fill
                priority
                sizes="(max-width: 1024px) 100vw, 1024px"
                className="object-cover rounded-lg"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex items-center justify-center transition-transform rounded-full shadow-lg size-10 lg:size-20 bg-videomule-green group-hover:scale-110">
                  <Play
                    className="text-white size-4 lg:size-8"
                    fill="white"
                  />
                </div>
              </div>
            </button>
          )}
        </div>

        <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-white/20 to-transparent rounded-2xl"></div>
      </motion.div>
    </section>
  );
}
